/**
 * Parse Utilities
 *
 * Functions for parsing pasted lists of values from Excel/CSV.
 * Handles deduplication, validation, and type checking.
 */

import type { ParseResult } from './advancedFiltersTypes'

/**
 * UUID validation regex (case-insensitive).
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Parse a pasted list of values into a validated array.
 * Handles multiple separators (newline, comma, tab, semicolon).
 * Deduplicates values and validates based on field type.
 *
 * @param text - Raw pasted text
 * @param fieldType - Field data type for validation
 * @returns Parse result with values and statistics
 */
export function parseValueList(
  text: string,
  fieldType: 'string' | 'uuid' | 'numeric'
): ParseResult {
  if (!text.trim()) {
    return {
      values: [],
      duplicatesRemoved: 0,
      emptyLinesSkipped: 0,
      invalidValues: [],
    }
  }

  // Split by newline, comma, tab, or semicolon (Excel-friendly)
  const rawValues = text
    .split(/[\n,;\t]+/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0)

  // Count empty lines
  const emptyLinesSkipped = text.split('\n').filter((line) => !line.trim()).length

  // Validate based on field type
  const validValues: string[] = []
  const invalidValues: string[] = []

  rawValues.forEach((value) => {
    if (fieldType === 'uuid' && !isValidUUID(value)) {
      invalidValues.push(value)
    } else if (fieldType === 'numeric' && !isValidNumeric(value)) {
      invalidValues.push(value)
    } else {
      validValues.push(value)
    }
  })

  // Deduplicate (case-sensitive for strings, UUIDs)
  const uniqueValues = Array.from(new Set(validValues))
  const duplicatesRemoved = validValues.length - uniqueValues.length

  return {
    values: uniqueValues,
    duplicatesRemoved,
    emptyLinesSkipped,
    invalidValues,
  }
}

/**
 * Validate if a string is a valid UUID.
 *
 * @param value - String to validate
 * @returns True if valid UUID
 */
export function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value)
}

/**
 * Validate if a string is a valid number.
 *
 * @param value - String to validate
 * @returns True if valid number
 */
export function isValidNumeric(value: string): boolean {
  if (value === '') return false
  const num = Number(value)
  return !isNaN(num) && isFinite(num)
}

/**
 * Format parse result for display feedback.
 * Generates user-friendly message about parse results.
 *
 * @param result - Parse result
 * @returns Formatted message
 */
export function formatParseResultMessage(result: ParseResult): string {
  const parts: string[] = []

  if (result.values.length > 0) {
    parts.push(`${result.values.length} value${result.values.length !== 1 ? 's' : ''} parsed`)
  }

  if (result.duplicatesRemoved > 0) {
    parts.push(
      `${result.duplicatesRemoved} duplicate${result.duplicatesRemoved !== 1 ? 's' : ''} removed`
    )
  }

  if (result.emptyLinesSkipped > 0) {
    parts.push(
      `${result.emptyLinesSkipped} empty line${result.emptyLinesSkipped !== 1 ? 's' : ''} skipped`
    )
  }

  if (result.invalidValues.length > 0) {
    parts.push(
      `${result.invalidValues.length} invalid value${result.invalidValues.length !== 1 ? 's' : ''}`
    )
  }

  return parts.join(' | ')
}

/**
 * Get performance warning level for large lists.
 *
 * @param valueCount - Number of values
 * @returns Warning level: null, 'info', or 'warning'
 */
export function getPerformanceWarningLevel(
  valueCount: number
): 'none' | 'info' | 'warning' {
  if (valueCount >= 5000) return 'warning'
  if (valueCount >= 1000) return 'info'
  return 'none'
}

/**
 * Get performance warning message for large lists.
 *
 * @param valueCount - Number of values
 * @returns Warning message
 */
export function getPerformanceWarningMessage(valueCount: number): string | null {
  const level = getPerformanceWarningLevel(valueCount)

  if (level === 'warning') {
    return `Large list (${valueCount.toLocaleString()} values). Lists over 5,000 values may cause slow queries. Consider using a saved filter or contact support for data mapping options.`
  }

  if (level === 'info') {
    return `Large list (${valueCount.toLocaleString()} values). Large lists may slow down query results. This is normal for bulk operations.`
  }

  return null
}
